import Image from 'next/image';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';

import { FlowCta } from 'src/components/jump-page/flow-cta';
import {
  EntityName,
  EventType,
  SectionName,
  TrkLocation,
} from 'src/utils/analytics/constants';
import { useBrandedLPAnalytics } from '../context';
import { useImpressionEvent } from 'src/utils/analytics/hooks';

type CardProps = {
  src: string;
  description: string | JSX.Element;
};

const texts = Object.freeze({
  ctaHeading: 'Get your credit card recommendations',
  ctaLabel: 'Get started',
});

const Card = ({ src, description }: CardProps) => (
  <Box maxWidth="300px" height="100%" flex={1} minWidth="200px">
    <Box
      paddingTop={{ xs: 1, xl: 4 }}
      paddingBottom={{ xs: 1, xl: 11 }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={3.5}
    >
      <Box maxWidth="100%">
        <Image
          src={src}
          alt=""
          style={{ width: '100%', height: 'auto' }}
          width={0}
          height={0}
          sizes="100vw"
          unoptimized
        />
      </Box>
      <Box
        width="100%"
        height="81px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        justifyContent="flex-start"
      >
        <Typography>{description}</Typography>
      </Box>
    </Box>
  </Box>
);

const cards = Object.freeze([
  {
    imageUrl: 'https://www.nerdwallet.com/cdn/cardfinder/insights-1.svg',
    description: 'Customers per month shop our credit cards marketplace.',
  },
  {
    imageUrl: 'https://www.nerdwallet.com/cdn/cardfinder/insights-2.svg',
    description: 'Hours spent by experts reviewing cards.',
  },
  {
    imageUrl: 'https://www.nerdwallet.com/cdn/cardfinder/insights-3.svg',
    description: (
      <>
        To{' '}
        <Link href="https://nerdwallet.com/recommend" target="_blank">
          complete the questionnaire
        </Link>{' '}
        and see your results so you can apply today.
      </>
    ),
  },
]);

export const BottomInsightsSection = () => {
  const { navigationId, impressionId } = useBrandedLPAnalytics();

  const { ref } = useImpressionEvent({
    eventType: EventType.ElementImpression,
    navigationId: navigationId,
    payload: {
      entity_name: EntityName.Flow,
      section_name: SectionName.JumpBottomCta,
      section_position: 2,
      parent_impression_id: impressionId,
    },
  });

  return (
    <Box
      ref={ref}
      paddingY={{ xs: 5, xl: 11 }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={3}
      margin="0 auto"
      width={{ xs: '90%', xl: '68%' }}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-around"
        flexWrap="wrap"
        width="100%"
      >
        {cards.map((card) => {
          return (
            <Card
              key={card.imageUrl}
              src={card.imageUrl}
              description={card.description}
            />
          );
        })}
      </Box>
      <Box
        bgcolor="blue.lightest"
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX={{ xs: 3, xl: 10 }}
        paddingY={{ xs: 6, xl: 10 }}
        gap={4}
      >
        <Typography
          fontSize={{ xs: 24, xl: 30 }}
          fontWeight={500}
          textAlign="center"
        >
          {texts.ctaHeading}
        </Typography>
        <FlowCta
          trkLocation={TrkLocation.JumpPageBottom}
          sx={{
            borderRadius: 1,
            height: '44px',
            paddingRight: 3,
            maxWidth: '358px',
          }}
        >
          <Typography fontSize={14} variant="accentSmall">
            {texts.ctaLabel}
          </Typography>
        </FlowCta>
      </Box>
    </Box>
  );
};
